import React, { useCallback, useEffect, useState } from 'react';
import { Search, MapPin, Phone, BellRing, StickyNote, ChevronRight, Loader2 } from 'lucide-react';
import { Card } from '../../ui/card';
import { Input } from '../../ui/input';
import { Skeleton } from '../../ui/skeleton';
import { easerGet } from '../../../lib/easerApi';
import { EmptyLine, fmtDay } from './crmShared';

/** Recherche d'un client (tiers Dolibarr) + clients suivis récemment ; ouverture de la fiche. */
export function CrmClients({ onOpenClient, onUnauthorized }) {
  const [q, setQ] = useState('');
  const [data, setData] = useState(null);
  const [searching, setSearching] = useState(false);

  const load = useCallback(async (query) => {
    setSearching(true);
    try { setData((await easerGet('/api/easer/crm/clients', { q: query })).data); } catch (err) {
      if (err?.response?.status === 401) return onUnauthorized();
      setData({ items: [], mode: query.length >= 2 ? 'search' : 'recent' });
    } finally { setSearching(false); }
  }, [onUnauthorized]);

  useEffect(() => {
    const query = q.trim();
    if (query.length === 1) return undefined;
    const t = setTimeout(() => load(query), query ? 350 : 0);
    return () => clearTimeout(t);
  }, [q, load]);

  const items = data?.items || [];
  return (
    <div className="space-y-3" data-testid="crm-clients">
      <div className="relative">
        <Search className="h-4 w-4 absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
        <Input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Nom, téléphone, e-mail ou ville…" className="pl-9 h-12 text-base" inputMode="search" autoComplete="off" data-testid="crm-clients-search" />
        {searching && <Loader2 className="h-4 w-4 absolute right-3 top-1/2 -translate-y-1/2 animate-spin text-muted-foreground" />}
      </div>
      <p className="text-xs text-muted-foreground">{data?.mode === 'search' ? `${items.length} client(s) trouvé(s) dans Dolibarr` : 'Clients suivis récemment (notes et rappels)'}</p>
      {!data && <div className="space-y-2">{[0, 1, 2].map((i) => <Skeleton key={i} className="h-16 rounded-xl" />)}</div>}
      {data && items.length === 0 && (
        <EmptyLine testId="crm-clients-empty">{data.mode === 'search' ? 'Aucun client ne correspond. Essayez un autre nom ou une ville.' : 'Aucun client suivi pour le moment : recherchez un client pour ouvrir sa fiche et ajouter une note.'}</EmptyLine>
      )}
      {items.length > 0 && (
        <ul className="space-y-2" data-testid="crm-clients-list">
          {items.map((c) => (
            <li key={c.id}>
              <button type="button" onClick={() => onOpenClient(c.id, c.name)} className="w-full text-left" data-testid={`crm-client-${c.id}`}>
                <Card className="p-3 flex items-center gap-3 hover:border-[hsl(var(--primary))]/40 transition-colors">
                  <span className="h-10 w-10 rounded-full bg-[hsl(var(--primary))]/10 text-[hsl(var(--primary))] font-semibold flex items-center justify-center flex-shrink-0">{(c.name || '?').slice(0, 1).toUpperCase()}</span>
                  <div className="min-w-0 flex-1">
                    <p className="font-medium truncate">{c.name}</p>
                    <p className="text-xs text-muted-foreground truncate flex items-center gap-2">
                      {c.town && <span className="inline-flex items-center gap-0.5"><MapPin className="h-3 w-3" /> {c.town}</span>}
                      {c.phone && <span className="inline-flex items-center gap-0.5"><Phone className="h-3 w-3" /> {c.phone}</span>}
                      {c.last_note_at && <span>dernière note {fmtDay(c.last_note_at)}</span>}
                    </p>
                  </div>
                  {c.open_reminders > 0 && <span className="inline-flex items-center gap-1 text-xs text-[hsl(var(--brand-ink))] bg-[hsl(var(--brand-apricot))]/20 rounded-full px-2 py-0.5"><BellRing className="h-3 w-3" /> {c.open_reminders}</span>}
                  {c.notes > 0 && !c.open_reminders && <span className="inline-flex items-center gap-1 text-xs text-muted-foreground"><StickyNote className="h-3 w-3" /> {c.notes}</span>}
                  <ChevronRight className="h-4 w-4 text-muted-foreground" />
                </Card>
              </button>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}
